/**
 * Admin console sign-in.
 *
 * A correct password is not sufficient: a user without platform-admin rights
 * is refused here and pointed at the main application. The same check happens
 * on every administrative endpoint, so this screen exists to give a clear
 * message rather than to keep anyone out.
 *
 * Two halves on a wide screen, as NexERP's own sign-in has: the console's
 * identity — what it is for — and the form. On a narrow one the form stands
 * alone.
 */
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, ErrorAlert, Icon, Input, PasswordInput } from '../../components/index.js';
import { useAdminAuth } from '../../context/AdminAuthContext.jsx';
import { useMutation } from '../../hooks/useApi.js';
import { BrandMark } from '../../layouts/Brand.jsx';

/** What the console is for, as its navigation groups it. */
const AREAS = [
  { icon: 'organization', name: 'Organizations', desc: 'Onboard customers and their owners' },
  { icon: 'module-request', name: 'Module requests', desc: 'Approve what organizations ask for' },
  { icon: 'module', name: 'Module catalog', desc: 'The ERP areas on offer' },
  { icon: 'health', name: 'Service health', desc: 'Every backend service, live' },
];

function Wordmark({ size = 28 }) {
  return (
    <span className="auth-hero-brand">
      <BrandMark size={size} />
      <span className="brand-word">
        Nex<span className="brand-word-strong">ERP</span>
      </span>
      <span className="brand-tag">Admin</span>
    </span>
  );
}

function ConsoleHero() {
  return (
    <aside className="auth-hero auth-hero-console" aria-hidden="true">
      <Wordmark />
      <div className="auth-hero-copy">
        <h2 className="auth-hero-title">
          The console behind <span>every NexERP workspace.</span>
        </h2>
        <p className="auth-hero-text">
          Organizations, the modules and plans they run on, and the health of the platform that serves them.
        </p>
        <div className="auth-areas">
          {AREAS.map((area, index) => (
            <div key={area.name} className="auth-area" style={{ animationDelay: `${120 + index * 60}ms` }}>
              <span className="auth-area-icon">
                <Icon name={area.icon} size={16} />
              </span>
              <div>
                <div className="auth-area-name">{area.name}</div>
                <div className="auth-area-desc">{area.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <p className="auth-hero-foot">NexERP platform administration</p>
    </aside>
  );
}

export function AdminLoginPage() {
  const { signIn } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({ email: '', password: '' });

  const { mutate, pending, error, fieldErrors } = useMutation(async () => {
    await signIn(form);
    navigate(location.state?.from?.pathname ?? '/', { replace: true });
  });

  const wrongConsole = error?.name === 'NotPlatformAdminError';
  const tenantAppUrl = import.meta.env.VITE_TENANT_APP_URL ?? 'http://localhost:5173';

  return (
    <div className="auth-shell">
      <ConsoleHero />
      <main className="auth-panel">
        <div className="auth-card">
          <div className="auth-brand auth-brand-mobile">
            <BrandMark size={30} />
            <span className="brand-word">
              Nex<span className="brand-word-strong">ERP</span>
            </span>
            <span className="brand-tag">Admin</span>
          </div>

          <Card>
            <form
              className="stack"
              onSubmit={(event) => {
                event.preventDefault();
                mutate().catch(() => {});
              }}
              noValidate
            >
              <div>
                <span className="auth-restricted">
                  <Icon name="lock" size={12} />
                  Platform staff only
                </span>
                <h1 className="auth-title">Platform administration</h1>
                <p className="auth-subtitle">
                  Restricted to platform staff. Organization users should sign in to the main application.
                </p>
              </div>

              {wrongConsole ? (
                <Alert variant="warning" title="Wrong console">
                  Those credentials are valid, but this console is for platform administrators.{' '}
                  <a href={tenantAppUrl}>Open the main application</a> instead.
                </Alert>
              ) : (
                <ErrorAlert error={error} />
              )}

              <Input
                label="Email"
                type="email"
                autoComplete="email"
                required
                autoFocus
                value={form.email}
                error={fieldErrors.email}
                onChange={(event) => setForm({ ...form, email: event.target.value })}
              />

              <PasswordInput
                label="Password"
                autoComplete="current-password"
                required
                value={form.password}
                error={fieldErrors.password}
                onChange={(event) => setForm({ ...form, password: event.target.value })}
              />

              <Button type="submit" variant="primary" size="lg" block loading={pending}>
                Sign in
              </Button>

              <p className="auth-footer text-xs">
                The first platform administrator is created by seeding the database. See the backend README.
              </p>
            </form>
          </Card>
        </div>
      </main>
    </div>
  );
}
